import { Settings, Trophy, Shield, Users, Bug, Layers, MapPin, Tag, UserCheck, Activity, LogOut } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';
import { EDITOR_TABS, getTabLabel, type EditorTab } from '@/types/routes';

const TAB_ICONS: Record<Exclude<EditorTab, 'login'>, typeof Trophy> = {
  'races': Trophy,
  'tracks': MapPin,
  'categories': Tag,
  'drivers': UserCheck,
  'profiles': Layers,
  'users': Users,
  'bugs': Bug,
  'monitoring': Activity,
  'settings': Settings,
};

export function Header() {
  const { tab, navigate } = useRouter();
  const { t } = useI18n();
  const { signOut } = useAuth();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 nav-bar h-[60px] sm:h-[52px]">
      <div className="h-full max-w-[960px] mx-auto px-5 sm:px-8 flex items-center">
        <a href="#/" className="flex items-center gap-2.5 text-text-primary shrink-0">
          <Shield size={18} className="text-accent shrink-0" />
          <span className="text-sm font-semibold tracking-tight">{t('nav.administration')}</span>
        </a>

        {/* Nav links */}
        <nav className="flex items-center gap-1 ml-4 sm:ml-8 overflow-x-auto">
          {EDITOR_TABS.map((tb) => {
            const Icon = TAB_ICONS[tb as Exclude<EditorTab, 'login'>];
            const isActive = tab === tb;
            return (
              <button
                key={tb}
                onClick={() => navigate(tb)}
                title={getTabLabel(tb as Exclude<EditorTab, 'login'>, t)}
                className={`
                  flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                  ${isActive ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                `}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{getTabLabel(tb as Exclude<EditorTab, 'login'>, t)}</span>
              </button>
            );
          })}
        </nav>

          <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => navigate('settings')}
              title={getTabLabel('settings', t)}
              aria-label={getTabLabel('settings', t)}
              className={`
                flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                ${tab === 'settings' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
              `}
            >
              <Settings size={14} />
            </button>
            <button
              onClick={signOut}
              title={t('settings.signOut')}
              aria-label={t('settings.signOut')}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
            >
              <LogOut size={14} />
            </button>
          </div>
      </div>
    </header>
  );
}
